import { useEffect, useState } from "react";
import { Eye, Loader2 } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";

/**
 * Live scan progress overlay. Shown while a rescan is in flight.
 *
 * Why staged messages? The /rescan API doesn't stream progress (yet), so we
 * cycle through six explanatory messages so the user knows what Yusi is
 * actually doing. The cycle adapts to elapsed time — earlier steps move
 * quicker, later steps linger so the user doesn't see the spinner stop on
 * "calculating new score" while we wait the last few seconds for the LLM.
 *
 * The overlay does NOT fabricate results — it only describes the categories
 * of work happening server-side. If the rescan errors out, the parent
 * surfaces a toast; this overlay just unmounts on `open={false}`.
 */
export default function RescanProgressOverlay({ open }) {
  const { t } = useLang();
  const [step, setStep] = useState(0);

  const steps = [
    t("scanProgress.step1"),
    t("scanProgress.step2"),
    t("scanProgress.step3"),
    t("scanProgress.step4"),
    t("scanProgress.step5"),
    t("scanProgress.step6"),
  ];

  useEffect(() => {
    if (!open) {
      setStep(0);
      return;
    }
    // Staggered timing: 4s × 5 early steps, then linger on the last.
    const intervals = [4000, 4500, 5000, 5500, 6000];
    let cancelled = false;
    let idx = 0;
    const tick = () => {
      if (cancelled) return;
      idx = Math.min(idx + 1, steps.length - 1);
      setStep(idx);
      if (idx < steps.length - 1) {
        setTimeout(tick, intervals[idx] ?? 6000);
      }
    };
    const handle = setTimeout(tick, intervals[0]);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/85 backdrop-blur-md px-6"
      data-testid="rescan-progress-overlay"
      aria-live="polite"
      role="status"
    >
      <div className="glass-strong rounded-3xl p-8 sm:p-10 max-w-md w-full text-center border border-cyan-500/20 shadow-[0_0_60px_rgba(34,211,238,0.18)]">
        {/* Animated radar/lens around the eye */}
        <div className="relative w-24 h-24 mx-auto mb-6">
          <div className="absolute inset-0 rounded-full border-2 border-cyan-500/30 animate-ping" />
          <div className="absolute inset-2 rounded-full border-2 border-cyan-400/50 animate-pulse" />
          <div className="absolute inset-0 rounded-full border-t-2 border-cyan-300 animate-spin" />
          <div className="absolute inset-0 flex items-center justify-center">
            <Eye size={28} className="text-cyan-300" strokeWidth={1.6} />
          </div>
        </div>

        <div className="text-[11px] uppercase tracking-[0.25em] text-cyan-400 font-semibold mb-2">
          {t("scanProgress.title")}
        </div>

        <div
          className="font-display text-lg font-semibold text-white mb-5 min-h-[3rem] flex items-center justify-center"
          data-testid="rescan-progress-step"
        >
          {steps[step]}
        </div>

        {/* Step pips */}
        <div className="flex items-center justify-center gap-1.5 mb-5">
          {steps.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                i < step ? "w-6 bg-cyan-400" : i === step ? "w-8 bg-cyan-300" : "w-3 bg-white/15"
              }`}
            />
          ))}
        </div>

        <div className="flex items-center justify-center gap-2 text-xs text-slate-400 mb-4">
          <Loader2 size={12} className="animate-spin" />
          <span>{step + 1} / {steps.length}</span>
        </div>

        <p className="text-xs text-slate-400 leading-relaxed">{t("scanProgress.tagline")}</p>
      </div>
    </div>
  );
}
